import React, { useCallback, useEffect, useState } from 'react';
import { PromiseState } from 'react-refetch';
import { EntityTypes } from 'context/entityContext';
import capitalize from 'lodash.capitalize';
import delve from 'dlv';
import debounce from 'lodash.debounce';

import pluralize from 'helpers/pluralize';
import { entityTypeToApiType } from 'helpers/mapEntityTypes';

import useOffset from 'hooks/useOffset';
import useSearch from 'hooks/useSearch';

import TableRow from './TableRow';
import NumberLoading from './NumberLoading';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import Pagination from 'components/data-visualization/Table/Pagination';
import Table from 'components/data-visualization/Table/Table';
import TableBody from 'components/data-visualization/Table/Body';
import BaseNoData from 'components/styled/NoData/NoData';
import TableRowLoader from 'components/data-containers/BrowseEntities/TableRow/TableRow.loader';
import apiConnect from '../../../helpers/apiConnect';
import {
  ModalWrapper,
  ButtonBack,
  SearchField,
  SearchFieldContainer,
  VersionText,
  CaretBack,
} from './ViewRelatedMenu.styles';

import {
  IBaseFetchParameters,
  IDataSource,
  IFetchBrowseParams,
} from 'components/data-containers/BrowseEntities/shared/types';

import getBrowseVersionsUrl from 'url-constructors/getBrowseVersionsUrl';
import getBrowseEntityUrl from 'url-constructors/getBrowseEntityUrl';

interface IModalProps {
  browseEntityType: string;
  entityType: EntityTypes;
  entityIds: string[];
  searchText: string;

  // react-refetch items
  fetchBrowseData: any;
  fetchEntityVersions: any;
  entityFetch: PromiseState;
  versionFetch: PromiseState;
}

const dataSources = (): IDataSource => ({
  fetchBrowseData: (params: IFetchBrowseParams): { entityFetch: string } => ({
    entityFetch: getBrowseEntityUrl(params),
  }),
  fetchEntityVersions: (
    params: IBaseFetchParameters
  ): { versionFetch: string } => ({
    versionFetch: getBrowseVersionsUrl(params),
  }),
});

const Modal: React.FC<IModalProps> = (props) => {
  const [isOpenVersion, setIsOpenVersion] = useState<boolean>(false);
  const [versionsData, setVersionsData] = useState<any>(null);
  const [loadMoreNumber, setLoadMoreNumber] = useState<number>(5);

  const {
    browseEntityType,
    entityType,
    entityIds,

    // react-refetch items
    entityFetch = PromiseState.create(),
    versionFetch = PromiseState.create<any>(),
    fetchBrowseData,
    fetchEntityVersions,
    searchText,
  } = props;

  const isVersionType = browseEntityType === 'version';
  const browseFetch = isVersionType ? versionFetch : entityFetch;

  const apiType: any = entityTypeToApiType(
    isVersionType ? entityType : (browseEntityType as any)
  );

  const { offsetState, setOffset, setEntities, setInvalidate } = useOffset({
    limit: 30,
    increment: 30,
    entitiesKey: pluralize(apiType),
  });

  const {
    searchValue,
    inputValue,
    handleSearch,
    setInputValue,
    setSearchValue,
  } = useSearch();

  const { limit, offset, value: entityData } = offsetState;

  const parentEntity = delve(entityData, ['parent', apiType]);
  const entities = delve(entityData, pluralize(apiType));
  const countKey = `total${capitalize(apiType)}Count`;
  const entityCount = delve(entityData, countKey);

  const checkHighlighted = useCallback(
    (entity) => {
      if (!entityIds || entityIds.length === 0 || !parentEntity) return;
      return entity[parentEntity.type].id === entityIds[0];
    },
    [entityIds, parentEntity]
  );

  const entityTitle = pluralize(
    browseEntityType === 'album' ? 'release' : browseEntityType
  );

  const loadingMore = entities?.length < entityCount && !isOpenVersion;

  useEffect(() => {
    const args = {
      browseEntityType: pluralize(browseEntityType),
      entityType,
      entityIds,
      salesType: 'streaming',
      nameFilter: searchValue,
      limit,
      offset: isVersionType ? 0 : offset,
    };

    if (isVersionType) {
      fetchEntityVersions(args);
    } else {
      fetchBrowseData(args);
    }
  }, [
    fetchEntityVersions,
    browseEntityType,
    fetchBrowseData,
    isVersionType,
    searchValue,
    entityType,
    entityIds,
    offset,
    limit,
  ]);

  useEffect(() => {
    setInvalidate();
  }, [setInvalidate, searchValue, browseEntityType]);

  useEffect(() => {
    if (browseFetch.value) {
      setEntities(browseFetch.value);
    }
  }, [browseFetch, setEntities]);

  useEffect(() => {
    const newValue = versionFetch?.value?.[pluralize(browseEntityType)];
    if (newValue && !isVersionType) {
      setVersionsData(newValue);
    }
  }, [versionFetch, isVersionType]);

  const { pending: loading, reason } = browseFetch;

  const onVersionClick = debounce(
    (
      id: { id: number },
      entityType: { entityType: EntityTypes },
      loadMoreNumber
    ): any => {
      setLoadMoreNumber(loadMoreNumber > 7 ? 7 : loadMoreNumber);
      const args = {
        browseEntityType: pluralize(browseEntityType),
        entityType,
        entityIds: id,
        salesType: 'streaming',
        limit: 50, // just to make sure we load all versions
        offset: 0,
      };
      fetchEntityVersions(args);
      setIsOpenVersion(true);
    },
    10
  );

  const onClickBack = debounce((): void => {
    setIsOpenVersion(false);
    setInputValue('');
    setSearchValue('');
  }, 10);

  const pending = isOpenVersion ? versionFetch.pending : loading;

  return (
    <ModalWrapper marginLeft={isVersionType ? '-250px' : '0'}>
      <ErrorBoundary error={reason} errorInfo={reason ? reason.cause : null}>
        {!isOpenVersion && !isVersionType && (
          <SearchFieldContainer>
            <SearchField
              handleSearch={handleSearch}
              value={inputValue}
              placeholder={searchText}
            />
          </SearchFieldContainer>
        )}

        {isOpenVersion && (
          <>
            <ButtonBack onClick={onClickBack}>
              <CaretBack />
              &nbsp;&nbsp;Back
            </ButtonBack>
            {(pending || versionsData) && (
              <VersionText>
                {pending ? <NumberLoading /> : versionsData?.length}
                &nbsp;VERSIONS
              </VersionText>
            )}
          </>
        )}

        <AnimatedLoadingWrapper loading={pending}>
          <Table isBrowseTable entity={entityType}>
            <TableBody>
              {pending && !loadingMore ? (
                <TableRowLoader
                  rowCount={loadMoreNumber || 7}
                  columnCount={2}
                />
              ) : (
                ((isOpenVersion && versionsData && versionsData.length > 0) ||
                  (entities && entities.length > 0)) &&
                ((isOpenVersion && versionsData) || entities).map(
                  (entity: any): JSX.Element => {
                    const isHighlighted = checkHighlighted(entity);
                    return (
                      <TableRow
                        key={
                          entity[isVersionType ? 'track' : browseEntityType].id
                        }
                        entity={
                          entity[isVersionType ? 'track' : browseEntityType]
                        }
                        entityData={entityData}
                        isHighlighted={isHighlighted}
                        isVersionType={isVersionType || isOpenVersion}
                        onVersionClick={onVersionClick}
                      />
                    );
                  }
                )
              )}
            </TableBody>
          </Table>

          {!isOpenVersion &&
            entities &&
            entities.length === 0 &&
            (!searchValue ? (
              <BaseNoData>
                <p>Unfortunately there is no {entityTitle} available</p>
              </BaseNoData>
            ) : (
              searchValue && <p>No search results for ‘{searchValue}’.</p>
            ))}

          {!(pending && !loadingMore) &&
            !isOpenVersion &&
            entities &&
            entities.length > 0 && (
              <Pagination
                current={entities.length}
                total={entityCount}
                offset={30}
                hasMoreResults={entities.length < entityCount}
                onClick={setOffset}
              />
            )}
        </AnimatedLoadingWrapper>
      </ErrorBoundary>
    </ModalWrapper>
  );
};

export default apiConnect(dataSources as any)(Modal as any) as any;
